/**
 * non-working-day-utils — czysta logika ekranu „Dni wolne od pracy" (bez Reacta i bez API):
 * filtr widoku, etykiety i duplikaty dni, walidacja wartości wpisu, rzutowanie wpisów na siatkę
 * kalendarza. Wydzielone z `NonWorkingDaysTab`, żeby dało się je testować osobno i używać w oknie
 * (`NonWorkingDayFormModal`) oraz w komórce daty (`DayDateCell`) bez cyklicznych importów.
 */

import { type CalendarEvent, DEFAULT_ENTRY_COLOR } from '@/components/calendar/calendar-utils';
import type { NonWorkingDayDto, UpdateNonWorkingDayInput } from '@/lib/non-working-days/api';

// ─── Filtr widoku ───────────────────────────────────────────────────────────

const DAY_FILTERS = ['all', 'yearly', 'oneOff'] as const;
export type DayFilter = (typeof DAY_FILTERS)[number];

export function isDayFilter(value: string): value is DayFilter {
  return (DAY_FILTERS as readonly string[]).includes(value);
}

export function matchesFilter(repeatsYearly: boolean, filter: DayFilter): boolean {
  if (filter === 'all') return true;
  return filter === 'yearly' ? repeatsYearly : !repeatsYearly;
}

// ─── Helpery dat i duplikatów ───────────────────────────────────────────────

/** „25.12" dla wpisu „Co roku" (rok jest bez znaczenia), „29.03.2027" dla jednorazowego. */
export function formatDayLabel(day: string, repeatsYearly: boolean): string {
  const [year = '', month = '', date = ''] = day.split('-');
  return repeatsYearly ? `${date}.${month}` : `${date}.${month}.${year}`;
}

const ISO_DAY_PATTERN = /^(\d{4})-(\d{2})-\d{2}$/;

/**
 * Rok i miesiąc z dnia w formacie „RRRR-MM-DD". `month` jest liczony od zera (0–11), jak w
 * `Date#getMonth` i w kursorze kalendarza ekranu. Błędny format to błąd programisty (dzień pochodzi
 * z serwera, który go waliduje) — rzucamy zamiast zgadywać.
 */
export function parseIsoYearMonth(day: string): { year: number; month: number } {
  const match = ISO_DAY_PATTERN.exec(day);
  if (match === null) throw new Error(`Niepoprawny format dnia: "${day}" (oczekiwano RRRR-MM-DD)`);
  return { year: Number(match[1]), month: Number(match[2]) - 1 };
}

/** Część daty, po której wykrywamy duplikaty: „MM-DD" (co roku) albo pełna data. */
function dayOrderKey(entry: Pick<NonWorkingDayDto, 'day' | 'repeatsYearly'>): string {
  return entry.repeatsYearly ? entry.day.slice(5) : entry.day;
}

/** Czy inny wpis (poza `ignoreId`) zajmuje już ten sam dzień w tym samym trybie powtarzania. */
export function isDuplicate(
  entries: readonly NonWorkingDayDto[],
  candidate: Pick<NonWorkingDayDto, 'day' | 'repeatsYearly'>,
  ignoreId?: string,
): boolean {
  const key = dayOrderKey(candidate);
  return entries.some(
    (e) =>
      e.id !== ignoreId && e.repeatsYearly === candidate.repeatsYearly && dayOrderKey(e) === key,
  );
}

// ─── Wartości i walidacja wpisu ─────────────────────────────────────────────

/** Wartości formularza wpisu (wiersz roboczy listy i okno kalendarza). */
export interface DraftEntry {
  day: string;
  repeatsYearly: boolean;
  name: string;
}

export interface DraftErrors {
  day: string | null;
  name: string | null;
}

export const NO_DRAFT_ERRORS: DraftErrors = { day: null, name: null };

/** Wspólna walidacja nowego / edytowanego wpisu: wymagana data i nazwa, brak duplikatu dnia. */
export function validateEntryValues(
  values: DraftEntry,
  entries: readonly NonWorkingDayDto[],
  t: (key: string) => string,
  ignoreId?: string,
): DraftErrors {
  return {
    day:
      values.day === ''
        ? t('tenant.nonWorkingDays.dateRequired')
        : isDuplicate(entries, values, ignoreId)
          ? t('tenant.nonWorkingDays.duplicate')
          : null,
    name: values.name.trim() === '' ? t('tenant.nonWorkingDays.nameRequired') : null,
  };
}

export function hasErrors(errors: DraftErrors): boolean {
  return errors.day !== null || errors.name !== null;
}

/** Pola wpisu, które realnie się zmieniły — PATCH niesie tylko je (pusty PATCH się nie wysyła). */
export function changedFields(
  entry: NonWorkingDayDto,
  values: DraftEntry,
): UpdateNonWorkingDayInput {
  const changes: UpdateNonWorkingDayInput = {};
  if (values.day !== entry.day) changes.day = values.day;
  if (values.repeatsYearly !== entry.repeatsYearly) changes.repeatsYearly = values.repeatsYearly;
  if (values.name !== entry.name) changes.name = values.name;
  return changes;
}

// ─── Rzutowanie wpisów na kalendarz ─────────────────────────────────────────

/** Kolory pasków: „Co roku" = niebieski (domyślny kolor wpisów kalendarza), jednorazowe = bursztynowy. */
export const YEARLY_EVENT_COLOR = DEFAULT_ENTRY_COLOR;
export const ONE_OFF_EVENT_COLOR = 'rgb(var(--color-warning))';

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * Wpisy widoczne w kalendarzu dla wyświetlanego roku: „Co roku" trafia na ten sam dzień i miesiąc
 * wyświetlanego roku (29.02 w roku nieprzestępnym pomijamy), jednorazowy zostaje na swojej dacie.
 * Kolejność wejścia (z serwera: najpierw „Co roku", potem jednorazowe) zostaje zachowana.
 */
export function buildCalendarEvents(
  entries: readonly NonWorkingDayDto[],
  filter: DayFilter,
  year: number,
): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  for (const entry of entries) {
    if (!matchesFilter(entry.repeatsYearly, filter)) continue;
    let startKey = entry.day;
    if (entry.repeatsYearly) {
      const monthDay = entry.day.slice(5);
      if (monthDay === '02-29' && !isLeapYear(year)) continue;
      startKey = `${String(year).padStart(4, '0')}-${monthDay}`;
    }
    events.push({
      id: entry.id,
      label: entry.name,
      color: entry.repeatsYearly ? YEARLY_EVENT_COLOR : ONE_OFF_EVENT_COLOR,
      startKey,
      endKey: startKey,
    });
  }
  return events;
}
